import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, imgUrl } from "@/lib/api";
import { useShop } from "@/context/ShopContext";
import RegionBar from "@/components/RegionBar";
import { ArrowRight, ArrowDown, MapPin, Globe, Gift, Sparkles, Users, ShoppingBag } from "lucide-react";

const CATS = ["All", "T-Shirts", "Hoodies", "Children's Apparel", "Hats", "Tote Bags", "Accessories", "Limited Collections"];
const localChain = ["Design", "Local materials", "Ethiopian production", "Local printing", "Local packaging", "Local delivery", "Ethiopian opportunity"];
const podFlow = ["You order", "Payment processed", "Sent to fulfillment", "Produced", "Packaged", "Shipped to you", "Tracking sent"];

const Chain = ({ steps, accent = "#C79A4B" }) => (
  <div className="flex flex-col md:flex-row md:flex-wrap md:items-center gap-3">
    {steps.map((s, i) => (
      <React.Fragment key={s}>
        <span className="bg-white/5 border rounded-full px-4 py-2 text-sm font-semibold text-white/90 w-fit" style={{ borderColor: accent + "55" }}>{s}</span>
        {i < steps.length - 1 && (<><ArrowRight size={16} className="hidden md:block" style={{ color: accent }} /><ArrowDown size={14} className="md:hidden ml-4" style={{ color: accent }} /></>)}
      </React.Fragment>
    ))}
  </div>
);

export default function ShopPage() {
  const { region, chooseRegion, priceLabel, img, COUNTRIES } = useShop();
  const [products, setProducts] = useState([]);
  const [cat, setCat] = useState("All");

  useEffect(() => {
    api.get(`/shop/products?market=${region.market}`).then(({ data }) => setProducts(data)).catch(() => {});
  }, [region.market]);

  const shown = cat === "All" ? products : products.filter((p) => p.category === cat);
  const shopAs = (market) => {
    chooseRegion(COUNTRIES.find((c) => c.market === market) || COUNTRIES[1]);
    setTimeout(() => document.getElementById("catalog")?.scrollIntoView({ behavior: "smooth" }), 60);
  };

  return (
    <div data-testid="shop-page" className="bg-[#FAF7F0] text-[#14241C]">
      <RegionBar />

      {/* HERO */}
      <section className="relative overflow-hidden bg-[#14241C] text-white">
        <img src="https://images.unsplash.com/photo-1523381210434-271e8be1f52b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" alt="Supporters wearing Refugium merchandise" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#14241C] via-[#14241C]/90 to-[#14241C]/40" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <p className="text-xs tracking-[0.25em] uppercase font-bold text-[#E0A526] mb-4">Refugium Shop</p>
          <h1 className="font-display text-6xl sm:text-7xl font-bold tracking-tight leading-[0.92]">Wear the Mission.</h1>
          <p className="text-lg text-[#E0A526] font-semibold mt-4">Shop Global. Support Local. Create Opportunity.</p>
          <p className="text-white/75 mt-5 max-w-2xl leading-relaxed">Refugium merchandise is about more than what we wear. Our goal is a model that connects supporters worldwide while creating economic opportunity in Ethiopia — working with local producers whenever possible, and fulfilling closer to our diaspora and international community.</p>
          <div className="flex flex-wrap gap-4 mt-9">
            <button onClick={() => shopAs("ethiopia")} data-testid="shop-eth-btn" className={`rounded-full px-8 py-4 font-bold transition-colors ${region.market === "ethiopia" ? "bg-[#E0A526] text-[#14241C]" : "bg-white/10 border border-white/40 text-white hover:bg-[#E0A526] hover:text-[#14241C]"}`}>Shop From Ethiopia</button>
            <button onClick={() => shopAs("global")} data-testid="shop-global-btn" className={`rounded-full px-8 py-4 font-semibold transition-colors ${region.market === "global" ? "bg-[#E0A526] text-[#14241C]" : "border border-white/40 text-white hover:bg-white hover:text-[#14241C]"}`}>Shop Internationally</button>
          </div>
          <p className="text-white/50 text-sm mt-6">One Mission. One Community. Across Borders.</p>
        </div>
      </section>

      {/* CHOOSE REGION */}
      <section id="choose" className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <h2 className="font-display text-3xl sm:text-4xl font-semibold text-center mb-10">Where are you shopping from?</h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-[#1F4D2E] text-white rounded-3xl p-9">
            <p className="text-4xl mb-2">🇪🇹</p>
            <h3 className="font-display text-2xl font-semibold mb-1">Ethiopia — Shop Locally</h3>
            <p className="text-white/70 text-sm mb-5">Locally produced merchandise whenever possible.</p>
            <ul className="text-sm space-y-1.5 text-white/85 mb-6">{["Local Production", "Local Jobs", "Local Pricing", "Local Payment Options", "Local Delivery"].map((b) => <li key={b} className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#E0A526]" />{b}</li>)}</ul>
            <button onClick={() => shopAs("ethiopia")} className="inline-flex items-center gap-2 bg-white text-[#1F4D2E] rounded-full px-6 py-3 text-sm font-bold">Shop Ethiopia <ArrowRight size={15} /></button>
          </div>
          <div className="bg-[#14241C] text-white rounded-3xl p-9">
            <p className="text-4xl mb-2">🌍</p>
            <h3 className="font-display text-2xl font-semibold mb-1">Diaspora & International</h3>
            <p className="text-white/70 text-sm mb-5">For supporters across the US, Canada, Europe, the UK, Australia and other supported countries.</p>
            <ul className="text-sm space-y-1.5 text-white/85 mb-6">{["International Fulfillment", "Regional Production Where Available", "Worldwide Delivery", "Support the Mission"].map((b) => <li key={b} className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#E0A526]" />{b}</li>)}</ul>
            <button onClick={() => shopAs("global")} className="inline-flex items-center gap-2 bg-[#E0A526] text-[#14241C] rounded-full px-6 py-3 text-sm font-bold">Shop Global <ArrowRight size={15} /></button>
          </div>
        </div>
      </section>

      {/* CATALOG */}
      <section id="catalog" className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
        <div className="flex flex-wrap gap-2 mb-8">
          {CATS.map((c) => (
            <button key={c} onClick={() => setCat(c)} data-testid={`cat-${c}`}
              className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${cat === c ? "bg-[#14241C] text-white" : "bg-white border border-[#14241C]/10 hover:border-[#14241C]/30"}`}>{c}</button>
          ))}
          <span className="rounded-full px-4 py-2 text-sm bg-[#B45B3E]/10 text-[#B45B3E] font-semibold">Made in Ethiopia · Coming Later</span>
        </div>
        {shown.length === 0 ? (
          <p className="text-center text-[#7a8a80] py-16" data-testid="no-shop-products">No products in this category yet.</p>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6" data-testid="shop-grid">
            {shown.map((p, i) => (
              <motion.div key={p.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: (i % 4) * 0.05 }}
                className="group bg-white rounded-2xl overflow-hidden border border-[#14241C]/8 hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)] transition-all" data-testid={`shop-product-${p.slug}`}>
                <Link to={`/shop/product/${p.slug}`}>
                  <div className="aspect-square overflow-hidden bg-[#F1EFE8] relative flex items-center justify-center">
                    {img(p) ? (
                      <img src={imgUrl(img(p))} alt={p.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <div className="flex flex-col items-center gap-2 text-[#a9b3ab]" data-testid={`shop-product-noimg-${p.slug}`}>
                        <ShoppingBag size={34} />
                        <span className="text-[11px] uppercase tracking-wider font-semibold">Mockup coming soon</span>
                      </div>
                    )}
                    {p.badges?.[0] && <span className="absolute top-3 left-3 bg-[#E0A526] text-[#14241C] text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full">{p.badges[0]}</span>}
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-[#14241C] leading-snug">{p.name}</h3>
                    <p className="text-xs text-[#7a8a80] mt-0.5">{p.colors?.length || 0} colors</p>
                    <p className="font-display text-lg font-bold mt-2">{priceLabel(p)}</p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </section>

      {/* MADE CLOSER TO HOME (Ethiopia) */}
      <section className="bg-[#14241C] text-white mt-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#E0A526] mb-3">Made closer to home</p>
          <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight mb-4">Supporting Ethiopian enterprise.</h2>
          <p className="text-white/70 max-w-2xl mb-10">Our goal is to produce Refugium merchandise for Ethiopian customers within Ethiopia whenever practical — working with local printers, producers, suppliers and workers so merchandise becomes another way to create local economic activity.</p>
          <Chain steps={localChain} />
          <p className="text-white/50 text-sm mt-8 max-w-2xl">Rather than shipping every product thousands of miles into Ethiopia, our long-term goal is to build local relationships capable of producing quality merchandise closer to the people buying it. <span className="text-[#E0A526]">(Our goal · where available · whenever practical.)</span></p>
        </div>
      </section>

      {/* LOCAL JOBS */}
      <section className="max-w-5xl mx-auto px-6 lg:px-10 py-24 text-center">
        <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mb-6">Your shirt can create work before it is even worn.</h2>
        <div className="flex flex-wrap justify-center gap-2.5 mb-8">{["Printing", "Sewing", "Textiles", "Embroidery", "Packaging", "Design", "Photography", "Warehousing", "Delivery", "Small businesses"].map((t) => <span key={t} className="bg-white border border-[#14241C]/10 rounded-full px-4 py-2 text-sm">{t}</span>)}</div>
        <p className="font-display text-2xl sm:text-3xl font-bold text-[#B45B3E]">When possible, keep more of the value local.</p>
      </section>

      {/* BUY HERE, IMPACT THERE */}
      <section className="grid md:grid-cols-2">
        <div className="bg-[#1F4D2E] text-white p-12 lg:p-16 flex flex-col justify-center">
          <Globe size={30} className="text-[#E0A526] mb-4" />
          <h2 className="font-display text-4xl font-semibold mb-4">Shop global. Support local.</h2>
          <p className="text-white/80 leading-relaxed">A Refugium supporter may live thousands of miles from Ethiopia, but their participation can still contribute to something happening closer to home. Our long-term model connects global demand with local opportunity.</p>
        </div>
        <div className="bg-[#B45B3E] text-white p-12 lg:p-16 flex flex-col justify-center">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-white/70 mb-3">Global fulfillment</p>
          <p className="text-white/90 mb-5">International orders can be produced through regional print-on-demand where available — no large inventories, delivered close to you.</p>
          <div className="flex flex-col gap-2">{podFlow.map((s) => <span key={s} className="text-sm inline-flex items-center gap-2"><ArrowRight size={13} /> {s}</span>)}</div>
        </div>
      </section>

      {/* FUTURE FEATURES */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid md:grid-cols-3 gap-6">
        {[
          { icon: Gift, t: "Gift a Shirt in Ethiopia", d: "Buy there. Produce there. Give there — help fund a locally produced shirt for someone in Ethiopia.", tag: "Coming Soon" },
          { icon: Sparkles, t: "Made in Ethiopia", d: "Collections increasingly designed, produced, sewn, printed and packaged in Ethiopia — and offered to the world.", tag: "Future Collection" },
          { icon: Users, t: "Meet the Makers", d: "Each Ethiopian-made product will one day introduce the producer, printer or cooperative behind it.", tag: "Future Feature" },
        ].map((f) => (
          <div key={f.t} className="bg-white border border-[#14241C]/10 rounded-2xl p-7">
            <div className="flex items-center justify-between mb-4">
              <span className="w-11 h-11 rounded-full bg-[#B45B3E]/12 text-[#B45B3E] flex items-center justify-center"><f.icon size={21} /></span>
              <span className="text-[10px] tracking-wider uppercase font-bold bg-[#B45B3E]/12 text-[#B45B3E] px-2.5 py-1 rounded-full">{f.tag}</span>
            </div>
            <h3 className="font-display text-xl font-semibold mb-2">{f.t}</h3>
            <p className="text-sm text-[#4a5a50] leading-relaxed">{f.d}</p>
          </div>
        ))}
      </section>
    </div>
  );
}
